import type { QuestionFillGapsSchema } from '@repo/schemas/quiz/questionFillGaps';

import { useCallback } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

export function useQuestionFillGapForm(gapIndex: number) {
  const form = useFormContext<QuestionFillGapsSchema>();

  const translateQuestionGapOptions = useFieldArray<QuestionFillGapsSchema>({
    control: form.control,
    name: `gaps.${gapIndex}.options`,
  });

  // Watch only the gap ID - this rarely changes
  const gapId = useWatch({
    control: form.control,
    name: `gaps.${gapIndex}.id`,
  }) as string | undefined;

  // Consolidated handler for adding new answer options
  const handleAppendAnswer = useCallback(() => {
    translateQuestionGapOptions.append({
      id: uuidv4(),
      content: '',
      correct: false,
      explanation: undefined,
    });
  }, [translateQuestionGapOptions]);

  const isCorrect = useCallback(
    (index: number): boolean => {
      return form.getValues(`gaps.${gapIndex}.options.${index}.correct`);
    },
    [form, gapIndex]
  );

  const toggleCorrect = (index: number): void => {
    const gapOptions = form.getValues(`gaps.${gapIndex}.options`);
    const option = gapOptions?.[index];

    if (!option) {
      return;
    }

    if (gapOptions.length === 2) {
      const nextSelectedCorrect: boolean = option.correct;
      const updatedOptions = gapOptions.map((currentOption, currentIndex) => ({
        ...currentOption,
        correct: currentIndex === index ? !nextSelectedCorrect : nextSelectedCorrect,
      }));

      form.setValue(`gaps.${gapIndex}.options`, updatedOptions, {
        shouldValidate: true,
        shouldDirty: true,
      });
      return;
    }

    const currentCorrectCount = gapOptions.reduce((sum, opt) => sum + (opt?.correct ? 1 : 0), 0);

    if (option.correct) {
      if (currentCorrectCount === 1) {
        toast.error('Co najmniej jedna opcja musi być poprawna', {
          duration: 1500,
          closeButton: false,
          position: 'top-center',
        });
        return;
      }
      form.setValue(`gaps.${gapIndex}.options.${index}`, { ...option, correct: false }, { shouldValidate: true, shouldDirty: true });
    } else {
      if (currentCorrectCount + 1 === gapOptions.length) {
        toast.error('Nie można zaznaczyć wszystkich odpowiedzi jako poprawne', {
          duration: 1500,
          closeButton: false,
          position: 'top-center',
        });
        return;
      }
      form.setValue(`gaps.${gapIndex}.options.${index}`, { ...option, correct: true }, { shouldValidate: true, shouldDirty: true });
    }
  };

  const handleRemoveAnswer = (index: number): void => {
    const gapOptions = form.getValues(`gaps.${gapIndex}.options`);
    const option = gapOptions?.[index];

    // If removing a correct option, check if there's at least one other correct option
    if (option?.correct) {
      const correctCount = gapOptions.reduce((sum, opt) => sum + (opt?.correct ? 1 : 0), 0);

      if (correctCount === 1) {
        toast.error('Nie można usunąć jedynej poprawnej odpowiedzi', {
          duration: 1500,
          closeButton: false,
          position: 'top-center',
        });
        return;
      }
    }

    translateQuestionGapOptions.remove(index);
  };

  return {
    translateQuestionGapOptions,
    handleAppendAnswer,
    isCorrect,
    toggleCorrect,
    handleRemoveAnswer,
    gapId: gapId ?? `luka ${gapIndex + 1}`,
  };
}
